/**
 * Well-known chain addresses shared across the app.
 */
export const WETH_ADDRESS = '0x4200000000000000000000000000000000000006';

// hvTAKEOVER (Harberger Vote) grid — v2.1
export const HV_TAKEOVER_UGM_ADDRESS =
  (process.env.NEXT_PUBLIC_HV_UGM_ADDRESS || '').toLowerCase();
export const HV_TAKEOVER_GRID_ID = `${HV_TAKEOVER_UGM_ADDRESS}-${process.env.NEXT_PUBLIC_HV_GRID_ID || '0'}`;
export const HV_TAKEOVER_ADAPTER_ADDRESS =
  (process.env.NEXT_PUBLIC_HV_ADAPTER_ADDRESS || '').toLowerCase();
export const TAKEOVER_TOKEN_ADDRESS =
  (process.env.NEXT_PUBLIC_TAKEOVER_TOKEN_ADDRESS || '').toLowerCase();
export const HV_TAKEOVER_GOVERNANCE_MODULE =
  (process.env.NEXT_PUBLIC_HV_GOVERNANCE_MODULE || '').toLowerCase();

/**
 * `BoardroomKeeperFeeAdapter` — the keeper-fee sink registered on the
 * boardroom grid as a custom yield adapter. The 1% Deposit-Protection
 * keeper fee accumulates here and is flushed (USDC) or claimed by
 * `owner()` (non-USDC) via the boardroom UI's "Collect Fees" button.
 *
 * Lowercased to match viem's address comparison helpers across the
 * lobby panels that look up the adapter row in `useLobbyAdapters`.
 */
export const HV_KEEPER_FEE_ADAPTER_ADDRESS =
  (process.env.NEXT_PUBLIC_HV_KEEPER_FEE_ADAPTER_ADDRESS || '').toLowerCase();

/**
 * Asset hash registered against the boardroom grid for the keeper-fee
 * adapter. The UGM keys yield receipts by this hash, fixed once when the
 * `BoardroomKeeperFeeAdapter` was deployed and immutable thereafter — so
 * it's a hardcoded protocol constant here rather than yet another env var
 * to populate (the boardroom is a single Base-mainnet deployment). The
 * `NEXT_PUBLIC_HV_KEEPER_FEE_ADAPTER_ASSET_HASH` override is kept only for
 * a future BKFA redeploy / non-mainnet boardroom.
 *
 * NOTE: this is NOT `keccak256("hv-keeper-fee-V1")` — it was chosen at
 * deploy and registered onchain; use this exact value (it matches the BKFA
 * constructor arg in `deployments/deposit-protection/base-mainnet.json` and
 * the `receiveYieldERC20` asset-hash topic the boardroom UGM emits).
 * Lowercased for comparison against indexer `assetHash` / `adapter.id`.
 */
export const HV_KEEPER_FEE_ADAPTER_ASSET_HASH = (
  process.env.NEXT_PUBLIC_HV_KEEPER_FEE_ADAPTER_ASSET_HASH ||
  '0x46ca24ce2e3353fbcd7ec99ecc2b8f74f9f2741159edd47643eb320c7d2485ee'
).toLowerCase();

/**
 * `ProtectionRedeemer` — the permissionless router for ERC-7715
 * (MetaMask Smart Account) Deposit-Protection delegations. The 7710
 * rail's analogue is `HV_KEEPER_FEE_ADAPTER_ADDRESS`; both contracts
 * expose the same `(permissionContexts[], modes[], executionCallDatas[])`
 * batch surface and both auto-flush USDC into the boardroom in the
 * same tx. The lobby's "Collect Fees" button targets one or both
 * depending on which rails have queued top-ups.
 *
 * Empty when the env var is unset (preview/test deploys without the
 * protection stack); call sites must gate on a truthy check before
 * encoding a redeem call.
 *
 * Lowercased so viem's address comparisons match across the keeper-fees
 * route and the boardroom panel.
 */
export const PROTECTION_REDEEMER_ADDRESS =
  (process.env.NEXT_PUBLIC_PROTECTION_REDEEMER_ADDRESS || '').toLowerCase();


/**
 * Intentional no-op kept for back-compat with existing call sites.
 *
 * Previously hid every grid on the v2.1 UGM except grid 0 (the
 * boardroom) — a launch-window safeguard while only the flagship
 * board was meant to be public. Lifted now that creators can launch
 * their own v2.1 grids via the wizard and we want them surfaced
 * everywhere v2.0 grids appear (lobby list, activity ticker, global
 * activity feed, pool fetchers).
 *
 * Per-grid moderation continues via the existing `getBannedGridSet`,
 * `isWhitelistedAddress` and `isBlockedName` mechanisms — flipping
 * this back to a real predicate would re-enable a blanket hide if
 * curation ever needs to re-emerge.
 *
 * `gridId` is kept in the signature so re-introducing the gate is a
 * one-line change (no call-site churn) and lints don't flip on the
 * unused parameter.
 */
export function isHiddenV21Grid(_gridId: string): boolean {
  return false;
}

// Takeover.fun Flaunch pool (the original pool on the markets page)
export const TAKEOVER_FLAUNCH_POOL_ADDRESS = '0x716f8e756f9277f8c9949926141c2666b86b5809';

// =============================================================================
// UGM v2.2 deployments (per `PLAN_UGM_V22_APP_INDEXER.md`)
// =============================================================================
//
// Each comma-separated address in NEXT_PUBLIC_V22_UGM_ADDRESSES is treated as
// a v2.2 manager. Anything not in this set defaults to v2.1 semantics
// (anti-snipe, eager seat init, etc.) — see `isV22Grid()`.
//
// Mainnet live deployment (post-`fa112f7` amountClaimed-cap fix):
//   UnifiedGridManagerV22         0xe4e476DdD3a5A5b676C404665e19E90F86214292
//   FlaunchYieldAdapter (V22)     0xFeF628613cf4bbccf749d31B233098DEf0d7b95b
//
// Earlier roll-out attempts are orphaned — they hold no state and lack the
// `_accumulateFees` / `_claimForSeat` / `_captureStrandedFeesAndSetBaseline`
// fix that prevents over-crediting `amountClaimed` when `_sendYieldCapped`
// truncates the transfer. DO NOT add these to NEXT_PUBLIC_V22_UGM_ADDRESSES:
//   - `0x1702…Ab22` UGM / `0x7F3f…bC61` adapter
//   - `0x16cDF33B…CC74a` UGM / `0x99204D11…E0525` adapter
//   - `0x7044…d480` UGM (never wired)
// See v2.2.0 release notes.
//
// The same env value MUST be wired into `takeover-indexer`'s config so both
// halves of the stack agree on a grid's version.

export const V22_UGM_ADDRESSES: ReadonlySet<string> = new Set(
  (process.env.NEXT_PUBLIC_V22_UGM_ADDRESSES ?? '')
    .split(',')
    .map((addr) => addr.trim().toLowerCase())
    .filter((addr) => addr.length > 0),
);

// =============================================================================
// UGM v2.3 deployments (per `PLAN_UGM_V23.md`)
// =============================================================================
//
// v2.3 is a strict superset of v2.2 — same lazy-seat / Dutch-auction
// surface, plus the `moduleTransferSeat` + extended `IGridHooksV23`
// callbacks, plus the two-axis pause (`paused` + `gridModulesPaused`)
// and per-module disable. Every v2.2 codepath in the app keeps working
// unchanged when pointed at a v2.3 manager; v2.3-only affordances
// (modules tab, weighted-yield badge) gate on `isV23Grid`.
//
// Mainnet deployment is provisioned via `script/56_DeployUGMv23.s.sol`
// (see takeover-contracts/). Until that lands, `NEXT_PUBLIC_V23_UGM_ADDRESSES`
// is empty and the launch wizard falls back to the V22 address.
//
// The same env value MUST be wired into `takeover-indexer`'s
// `MAINNET_V23_UGM_ADDRESS` so both halves of the stack agree on a
// grid's version (see `takeover-indexer/scripts/generate-config.js`).

export const V23_UGM_ADDRESSES: ReadonlySet<string> = new Set(
  (process.env.NEXT_PUBLIC_V23_UGM_ADDRESSES ?? '')
    .split(',')
    .map((addr) => addr.trim().toLowerCase())
    .filter((addr) => addr.length > 0),
);

/**
 * `true` if `managerAddress` is a known UGM v2.3 deployment.
 *
 * v2.3-specific code paths (modules tab, weighted yield badge,
 * `moduleTransferSeat` builders, two-axis pause UI) MUST gate on this
 * helper. The shared lazy-seat / Dutch-auction surface lives behind
 * `isV22OrLaterGrid`.
 *
 * Empty / non-Ethereum-address inputs always return `false`.
 */
export function isV23Grid(managerAddress: string | null | undefined): boolean {
  if (!managerAddress) return false;
  return V23_UGM_ADDRESSES.has(managerAddress.toLowerCase());
}

/**
 * Address used by the launch wizard for new boards. Defaults to the
 * V2.3 UGM allowlist (latest deployment) and falls back to V2.2 during
 * the v2.3 rollout window — see PLAN_UGM_V23.md §4 / Q1.
 *
 * Env precedence:
 *   1. `NEXT_PUBLIC_LAUNCH_UGM_ADDRESS` — explicit override, must be a
 *      registered V22 or V23 address. Useful in CI / preview deploys that
 *      point at a non-default instance.
 *   2. First entry of `NEXT_PUBLIC_V23_UGM_ADDRESSES` — preferred default
 *      once v2.3 is deployed.
 *   3. First entry of `NEXT_PUBLIC_V22_UGM_ADDRESSES` — fallback during
 *      the v2.3 rollout window.
 *   4. Empty — `useLaunchGrid` errors out cleanly with a config message.
 *
 * Lowercased for parity with viem's address comparison helpers.
 */
export const LAUNCH_UGM_ADDRESS: string = (() => {
  const explicit = (process.env.NEXT_PUBLIC_LAUNCH_UGM_ADDRESS ?? '').trim().toLowerCase();
  if (explicit) return explicit;
  const firstV23 = [...V23_UGM_ADDRESSES][0];
  if (firstV23) return firstV23;
  const firstV22 = [...V22_UGM_ADDRESSES][0];
  return firstV22 ?? '';
})();

/**
 * `true` if `managerAddress` is a known UGM v2.2 deployment (excludes v2.3).
 *
 * Empty / non-Ethereum-address inputs always return `false`. Case-
 * insensitive: callers don't have to lowercase first (we do it here).
 *
 * V22-specific code paths (Dutch auction overlay, lazy-seat synthesis,
 * V22-shaped createGrid, etc.) MUST be gated on this helper or
 * `isV22OrLaterGrid` for the v22-and-newer family. See
 * PLAN_UGM_V22_APP_INDEXER.md §1 (versioning model).
 */
export function isV22Grid(managerAddress: string | null | undefined): boolean {
  if (!managerAddress) return false;
  return V22_UGM_ADDRESSES.has(managerAddress.toLowerCase());
}

/**
 * `true` if `managerAddress` is v2.2 or v2.3. v2.3 is a strict superset
 * of v2.2's lazy-seat / Dutch-auction / governance-module surface, so
 * any code path that just needs "this is a v22-or-newer grid" should
 * use this rather than `isV22Grid`.
 */
export function isV22OrLaterGrid(managerAddress: string | null | undefined): boolean {
  return isV22Grid(managerAddress) || isV23Grid(managerAddress);
}

/**
 * `true` if the indexer's per-row `gridVersion` field denotes UGM v2.2.
 *
 * The indexer historically emitted three different shapes for the same
 * version — `'v2.2'`, `'2.2'`, and a bare `'2'` — depending on the
 * subgraph generator that produced the row. Some halves of the stack
 * matched all three (`envio-client.ts`, the per-user pending-fees
 * aggregator) while others matched only the canonical `'v2.2'`
 * (per-grid `route.ts`, `use-grid-page.ts`). When a row landed with
 * `'2.2'` or `'2'`, the per-grid API skipped `gridConfigV22`, the V22
 * yield-cap, and lazy-seat synthesis — but the per-user aggregator
 * still treated it as V22 — and the two halves disagreed about the
 * same grid.
 *
 * Use this helper everywhere we branch on the indexer string. The
 * address-keyed `isV22Grid(managerAddress)` is still the source of
 * truth when the `managerAddress` is in hand; this helper is the
 * fallback for the indexer-only call sites.
 */
export function isV22GridVersion(
  gridVersion: string | null | undefined,
): boolean {
  if (!gridVersion) return false;
  const v = gridVersion.trim().toLowerCase();
  return v === 'v2.2' || v === '2.2' || v === '2';
}
